/**
 * useTrainingProviders Hook
 * 
 * Fetches training providers and artifacts with provider data.
 * Provider-agnostic design - no special treatment for any provider.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface TrainingProvider {
  id: string;
  name: string;
  display_name: string;
  website_url: string | null;
  description: string | null;
  logo_url: string | null;
  trust_score: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Branding v1 fields
  short_description?: string | null;
  long_description?: string | null;
  primary_roles?: string[];
  delivery_formats?: string[];
  trust_badge?: string | null;
  branding_tone?: string | null;
  primary_focus_capabilities?: string[];
  // Phase 4 extensions
  provider_type?: string;
  delivery_modes?: string[];
  visibility_level?: string;
  trust_level?: string;
  requires_manual_review?: boolean;
  discovery_enabled?: boolean;
  discovery_paused_at?: string | null;
  hidden_from_recommendations?: boolean;
  takedown_reason?: string | null;
  takedown_requested_at?: string | null;
  provider_metadata?: Record<string, unknown>;
  learning_enabled?: boolean;
  lifecycle_status?: string;
}

/**
 * Fetch all active training providers
 */
export function useTrainingProviders() {
  return useQuery({
    queryKey: ['training-providers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_providers')
        .select('*')
        .eq('is_active', true)
        .order('display_name');

      if (error) throw error;
      return data as TrainingProvider[];
    },
  });
}

/**
 * Fetch a single training provider by ID
 */
export function useTrainingProvider(providerId: string | null) {
  return useQuery({
    queryKey: ['training-provider', providerId],
    queryFn: async () => {
      if (!providerId) return null;
      
      const { data, error } = await supabase
        .from('training_providers')
        .select('*')
        .eq('id', providerId)
        .single();

      if (error) throw error;
      return data as TrainingProvider;
    },
    enabled: !!providerId,
  });
}

/**
 * Extended artifact type with provider info
 */
export interface TrainingArtifactWithProvider {
  id: string;
  title: string;
  provider_name: string;
  instructor_name: string | null;
  artifact_type: string;
  access_url: string;
  access_type: string;
  estimated_time_minutes: number | null;
  primary_skill: string | null;
  skill_depth: string;
  teaching_styles: string[];
  delivery_format: string;
  travel_required: boolean;
  artifact_scope: string;
  confidence_level: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  provider_id: string | null;
  original_summary_short: string | null;
  provider: TrainingProvider | null;
  domains?: Array<{
    domain_key: string;
    is_primary: boolean;
    relevance_score: number;
  }>;
  primary_domain?: string;
}

/**
 * Fetch training artifacts with provider data
 */
export function useArtifactsWithProviders(options?: {
  domains?: string[];
  limit?: number;
  providerId?: string;
}) {
  const { domains, limit = 40, providerId } = options || {};

  return useQuery({
    queryKey: ['training-artifacts-with-providers', { domains, limit, providerId }],
    queryFn: async () => {
      // FIX-R116-HIGH-1c: same alphabetical-sort bug as R116-HIGH-1a/1b.
      // confidence_level DESC (string) sorted `provisional` first because
      // PostgREST orders strings alphabetically and 'p' > 'o' > 'c'. The
      // intended quality order is verified > community > provisional, so
      // the broken sort surfaced the lowest-confidence artifacts at the
      // top of the provider browse. Removed the bad order; created_at DESC
      // alone is deterministic, and downstream scorers apply confidence
      // tier as a multiplier where ranking matters.
      let query = supabase
        .from('training_artifacts')
        .select(`
          *,
          provider:training_providers(*)
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .order('id', { ascending: true })
        .limit(limit);

      if (providerId) {
        query = query.eq('provider_id', providerId);
      }

      const { data: artifacts, error: artifactError } = await query;
      if (artifactError) throw artifactError;
      if (!artifacts || artifacts.length === 0) return [];

      // Fetch domain mappings
      const artifactIds = artifacts.map(a => a.id);
      const { data: mappings, error: mapError } = await supabase
        .from('training_artifact_domain_map')
        .select('artifact_id, domain_key, is_primary, relevance_score')
        .in('artifact_id', artifactIds);

      if (mapError) throw mapError;

      // Combine artifacts with domain mappings
      let result = artifacts.map(artifact => {
        const artifactMappings = mappings?.filter(m => m.artifact_id === artifact.id) || [];
        const primaryMapping = artifactMappings.find(m => m.is_primary);
        
        return {
          ...artifact,
          domains: artifactMappings,
          primary_domain: primaryMapping?.domain_key,
        } as TrainingArtifactWithProvider;
      });

      // Filter by domains if specified
      if (domains && domains.length > 0) {
        const matchedIds = new Set(
          mappings?.filter(m => domains.includes(m.domain_key)).map(m => m.artifact_id) || []
        );
        result = result.filter(a => matchedIds.has(a.id));
      }

      return result;
    },
  });
}
